'use client';

/* eslint-disable @next/next/no-img-element
-- This component can optionally use Next.js Image component via the useNext prop. */

import { FunctionComponent } from 'react';

import { ImageModel } from '@/services/models/Media/Image';

import { InvalidArgumentError } from '@/utils/errors';

import { isNullish } from '@/utils/null-utils';
import NextImage from 'next/image';
import { CustomImage } from './internal/CustomImage';
import { IBaseImageProps } from './IBaseImageProps';

export interface IImageProps extends IBaseImageProps {
  /**
   * Determines if we want to use the `<Image />` component from Next. Defaults to `false`.
   */
  useNext?: boolean;
}

/**
 * First-party image component that can optionally use Next.js `Image` component or a
 * Custom image component with our own optimization logic.
 *
 * When useNext is true, manual optimization is skipped and Next.js Image handles
 * all optimization automatically.
 *
 * @throws An {@link InvalidArgumentError} if `width` and/or `height` is missing and `fill` is not set to true.
 */
export const Image: FunctionComponent<IImageProps> = ({
  image,
  width,
  height,
  fill = true,
  fit = 'contain',
  enhanceOnZoom: enhanceOnZoomProp = false,
  priority = false,
  optimization = 'auto',
  useSkeletonLoader = true,
  className,
  style,
  useNext = false
}) => {
  // Validate dimensions
  if (isNullish(width) && isNullish(height)) {
    // If there's no dimensions specified, verify if `fill` is true.
    if (!fill) {
      throw new InvalidArgumentError(
        '`Image` requires either both `width` and `height` to be present or `fill` to be true.'
      );
    }
  }

  const imageModel = ImageModel.from(image);

  if (useNext) {
    const { src, alt, caption } = imageModel;
    return (
      <NextImage
        src={src}
        alt={alt}
        title={caption ?? ''}
        priority={priority}
        fill={fill}
        width={width}
        height={height}
        style={{ objectFit: fit }}
        loading={priority ? 'eager' : undefined}
        fetchPriority={priority ? 'high' : undefined}
      />
    );
  }

  return (
    <CustomImage
      image={image}
      width={width}
      height={height}
      fill={fill}
      fit={fit}
      enhanceOnZoom={enhanceOnZoomProp}
      priority={priority}
      optimization={optimization}
      useSkeletonLoader={useSkeletonLoader}
      className={className}
      style={style}
    />
  );
};
